'use client'
import createOptimisticStateProvider from '@/components/context-factories/create-optimistic-state-provider'
import createStoreProvider from '@/components/context-factories/create-store-provider'
import withSuspendedContext from '@/components/context-factories/with-suspended-context'
import { refreshCart } from '@/lib/actions/refresh-cart'
import { CartContent } from '@/lib/api/shopware/cart/cart.types'
import { PropsWithChildren, startTransition, useCallback } from 'react'
import { create } from 'zustand'
import { devtools } from 'zustand/middleware'

interface CartStore {
  showCart: boolean
  toggleShowCart: () => void
}

const initialCartSidebar = () => {
  const store = create<CartStore>()(
    devtools(
      (set) => ({
        showCart: false,
        toggleShowCart: () => set((state) => ({ showCart: !state.showCart })),
      }),
      {
        name: 'cart-sidebar',
      },
    ),
  )

  return store
}

const reducer = (_state: CartContent | undefined, newState: CartContent | undefined) => {
  return newState
}

const [CartSidebarProvider, useCartSidebarStore] =
  createStoreProvider(initialCartSidebar)

const useCartSidebar = () => {
  return useCartSidebarStore()()
}

const [Provider, useProvider] = createOptimisticStateProvider(reducer)

type CartProviderProps = PropsWithChildren<{
  value?: CartContent
}>

const [CartStoreProvider, useCartProvider, withCartStoreSuspense] =
  withSuspendedContext(
    ({ value, children }: CartProviderProps) => (
      <Provider initialState={value}>{children}</Provider>
    ),
    useProvider,
  )

const useCartStore = () => {
  const { state } = useCartProvider()
  return state
}

const useSetCart = () => {
  const { dispatch } = useCartProvider()
  const setCart = useCallback(
    (cart: CartContent) => startTransition(() => dispatch(cart)),
    [dispatch],
  )
  return setCart
}

const useCartCount = () => {
  const cart = useCartStore()
  let total = 0
  for(const item of cart?.lineItems || []) {
    total += item.quantity
  }
  return total
}

export const useIsExperienceSet = () => {
  const cart = useCartStore()
  return cart?.lineItems.some(
    (item) => item.payload?.customFields?.is_experience_set,
  )
}

const useRefreshCart = () => {
  return refreshCart
}

const useClearCart = () => {
  const { dispatch } = useCartProvider()
  return useCallback(() => startTransition(() => dispatch(undefined)), [dispatch])
}

const useToggleShowCart = () => {
  const { toggleShowCart } = useCartSidebar()
  return toggleShowCart
}

export {
  useCartSidebar,
  CartSidebarProvider,
  CartStoreProvider,
  useSetCart,
  useClearCart,
  withCartStoreSuspense,
  useCartStore,
  useCartCount,
  useToggleShowCart,
  useRefreshCart,
}
